import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import styles from './EventParticipantsPage.module.css';
import { eventParticipantsApi } from '../../../services/eventParticipantsApi';

interface User {
  User_ID: string;
  firstname?: string;
  lastname?: string;
  title?: string;
  mail?: string;
  jobtitle?: string;
  picture?: string;
  profile?: string;
  linkedin?: string;
  city?: {
    name: string;
  };
  country?: {
    abbrev: string;
  };
  firm?: {
    abbrev: string;
  };
}

interface Event {
  Events_ID: string;
  title: string;
  sub_title?: string;
  event_date?: string;
  min_select_users: number;
  max_select_users: number;
  is_future: boolean;
}

interface EventParticipantsData {
  event: Event;
  users: User[];
  best_matches: any[];
  selections: any[];
  user_already_selected: boolean;
  matches_generated: boolean;
}

const EventParticipantsPage: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<EventParticipantsData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  // Add search and sorting state
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [sortByFirstName, setSortByFirstName] = useState<boolean>(false);

  useEffect(() => {
    if (eventId) {
      fetchEventParticipants();
    }
  }, [eventId]);

  const fetchEventParticipants = async () => {
    try {
      setLoading(true);
      setErrorMessage(''); // Clear any previous error
      const response = await eventParticipantsApi.getEventParticipants(eventId!);
      setData(response);
    } catch (error: any) {
      console.error('Error fetching event participants:', error);
      
      // Extract error message from backend response
      let displayErrorMessage = 'Failed to load event data. Please try again.';
      
      if (error.message.includes('404')) {
        displayErrorMessage = 'Event not found. The event may have been deleted or the URL is incorrect.';
        setErrorMessage(displayErrorMessage);
        toast.error(displayErrorMessage, {
          position: "top-right",
          autoClose: 5000,
          onClose: () => navigate('/events')
        });
      } else if (error.message.includes('403')) {
        // Check if the error message contains the specific backend message
        if (error.message.includes('You are not registered for this event')) {
          displayErrorMessage = 'You are not registered for this event';
        } else {
          displayErrorMessage = 'You are not registered for this event';
        }
        setErrorMessage(displayErrorMessage);
        toast.error(displayErrorMessage, {
          position: "top-right",
          autoClose: 5000,
          onClose: () => navigate('/events')
        });
      } else if (error.message.includes('401')) {
        displayErrorMessage = 'Please log in to access this page';
        setErrorMessage(displayErrorMessage);
        toast.error(displayErrorMessage, {
          position: "top-right",
          autoClose: 5000,
          onClose: () => navigate('/login')
        });
      } else {
        setErrorMessage(displayErrorMessage);
        toast.error(displayErrorMessage, {
          position: "top-right",
          autoClose: 5000
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUserSelection = (userId: string) => {
    const newSelectedUsers = new Set(selectedUsers);
    if (newSelectedUsers.has(userId)) {
      newSelectedUsers.delete(userId);
    } else {
      newSelectedUsers.add(userId);
    }
    setSelectedUsers(newSelectedUsers);
  };

  const getStatusMessage = () => {
    if (!data) return {
      message: '',
      type: 'error' as const,
      buttonText: 'Save Selection'
    };
    
    const selectedCount = selectedUsers.size;
    const { min_select_users, max_select_users } = data.event;

    if (selectedCount < min_select_users) {
      const remaining = min_select_users - selectedCount;
      return {
        message: `You must select at least ${min_select_users} users and at most ${max_select_users} users. Currently selected: ${selectedCount}.`,
        type: 'error' as const,
        buttonText: `You need ${remaining} more`
      };
    } else if (selectedCount > max_select_users) {
      return {
        message: `You can select at most ${max_select_users} users and at least ${min_select_users} users. Currently selected: ${selectedCount}.`,
        type: 'error' as const,
        buttonText: `Too many selected (${selectedCount}/${max_select_users})`
      };
    } else {
      return {
        message: `You have selected ${selectedCount} users. You can proceed.`,
        type: 'success' as const,
        buttonText: 'Save Selection'
      };
    }
  };

  const handleSaveSelection = async () => {
    if (!data || !eventId) return;

    const selectedCount = selectedUsers.size;
    const { min_select_users, max_select_users } = data.event;

    if (selectedCount < min_select_users) {
      toast.error(`You must select at least ${min_select_users} users. Currently selected: ${selectedCount}.`, {
        position: "top-center",
        autoClose: 5000
      });
      return;
    }

    if (selectedCount > max_select_users) {
      toast.error(`You can select at most ${max_select_users} users. Currently selected: ${selectedCount}.`, {
        position: "top-center",
        autoClose: 5000
      });
      return;
    }

    try {
      setSaving(true);
  const selectedUserIds = Array.from(selectedUsers);
      
      await eventParticipantsApi.saveUserSelection(eventId, selectedUserIds);
      
      toast.success('Selection saved successfully!', {
        position: "top-right",
        autoClose: 3000
      });

      // Navigate to voting page
      navigate(`/events/${eventId}/vote/`);
    } catch (error: any) {
      console.error('Error saving selection:', error);
      toast.error('Failed to save selection. Please try again.', {
        position: "top-right",
        autoClose: 5000
      });
    } finally {
      setSaving(false);
    }
  };

  const getDefaultProfileImage = () => {
    return '/static/images/user-profile-icon.png';
  };

  // Filter and sort users based on search term and sort preference
  const getFilteredAndSortedUsers = () => {
    if (!data?.users) return [];

    let filteredUsers = data.users;

    // Filter by search term
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filteredUsers = data.users.filter(user => {
        const fullName = `${user.title || ''} ${user.firstname || ''} ${user.lastname || ''}`.toLowerCase();
        const company = user.firm?.abbrev?.toLowerCase() || '';
        const jobTitle = user.jobtitle?.toLowerCase() || '';
        const location = `${user.city?.name || ''} ${user.country?.abbrev || ''}`.toLowerCase();

        return fullName.includes(searchLower) ||
               company.includes(searchLower) ||
               jobTitle.includes(searchLower) ||
               location.includes(searchLower);
      });
    }

    // Sort users
    return filteredUsers.sort((a, b) => {
      if (sortByFirstName) {
        // Sort by first name
        const nameA = (a.firstname || '').toLowerCase();
        const nameB = (b.firstname || '').toLowerCase();
        return nameA.localeCompare(nameB);
      } else {
        // Sort by company (default)
        const companyA = (a.firm?.abbrev || '').toLowerCase();
        const companyB = (b.firm?.abbrev || '').toLowerCase();
        return companyA.localeCompare(companyB);
      }
    });
  };

  if (loading) {
    return (
      <div className="Root">
        <div className="container mt-4">
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
        <ToastContainer />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="Root">
        <div className="container mt-4">
          <div className="alert alert-danger" role="alert">
            {errorMessage || 'Failed to load event data. Please try again.'}
          </div>
        </div>
        <ToastContainer />
      </div>
    );
  }

  const statusInfo = getStatusMessage();

  return (
    <div className="Root">
      <div className="container mt-4">
        {/* Breadcrumb Navigation */}
        <nav aria-label="breadcrumb">
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="/events/" className="text-decoration-none">Events</a>
            </li>
            <li className="breadcrumb-item">
              <span>{data.event.title}</span>
            </li>
            <li className="breadcrumb-item active">
              <span>Choose your Matches</span>
            </li>
          </ol>
        </nav>

        {/* Main Content Card */}
        <div className="card p-4" style={{ width: '100%' }}>
          <div className={styles.roleHeader}>
            <i className="bi bi-calendar3-event"></i>
            <h4 className="mb-0">
              Event - {data.event.title} - <strong>Choose your Matches</strong>
            </h4>
            {data.event.sub_title && (
              <p>
                <i 
                  className="bi bi-info text-primary" 
                  data-bs-toggle="popover" 
                  data-bs-placement="top"
                  data-bs-content={data.event.sub_title}
                  data-bs-html="true" 
                  tabIndex={0}
                ></i>
              </p>
            )}
          </div>
          <hr />

          {!data.user_already_selected ? (
            <>
              {/* Search and Sort Controls */}
              <div className="row mb-4">
                <div className="col-md-6">
                  <div className="input-group">
                    <span className="input-group-text">
                      <i className="bi bi-search"></i>
                    </span>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search participants (name, company, job title, location)..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    {searchTerm && (
                      <button
                        className="btn btn-outline-secondary"
                        type="button"
                        onClick={() => setSearchTerm('')}
                      >
                        <i className="bi bi-x"></i>
                      </button>
                    )}
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-check form-switch d-flex align-items-center">
                    <input
                      className="form-check-input me-2"
                      type="checkbox"
                      id="sortToggle"
                      checked={sortByFirstName}
                      onChange={(e) => setSortByFirstName(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="sortToggle">
                      Sort by: {sortByFirstName ? 'First Name (A-Z)' : 'Company (A-Z)'}
                    </label>
                  </div>
                </div>
              </div>

              {/* Results count */}
              {data.users.length > 0 && (
                <div className="row mb-2">
                  <div className="col-12">
                    <small className="text-muted">
                      Showing {getFilteredAndSortedUsers().length} of {data.users.length} participants
                      {searchTerm && ` matching "${searchTerm}"`}
                    </small>
                  </div>
                </div>
              )}

              {/* Selection Status */}
              <div className="row">
                <div className={styles.selectionStatus}>
                  <p className={`${styles.statusMessage} ${styles[statusInfo.type]}`}>
                    {statusInfo.message}
                  </p>
                </div>
              </div>

              {/* User Cards */}
              <div className={styles.cardContainer}>
                {getFilteredAndSortedUsers().length === 0 ? (
                  <div className="text-center py-5">
                    <i className="bi bi-search" style={{ fontSize: '3rem', color: '#6c757d' }}></i>
                    <p className="text-muted mt-3">
                      {searchTerm ? 'No participants found matching your search.' : 'No participants available.'}
                    </p>
                    {searchTerm && (
                      <button
                        className="btn btn-outline-primary"
                        onClick={() => setSearchTerm('')}
                      >
                        Clear search
                      </button>
                    )}
                  </div>
                ) : (
                  getFilteredAndSortedUsers().map((user) => (
                  <div key={user.User_ID} className={styles.participantCard}>
                    <div className={styles.participantCardHeader}>
                      <img
                        src={user.picture ? `data:image/png;base64,${user.picture}` : getDefaultProfileImage()}
                        className={styles.profilePicture}
                        alt="User Profile"
                      />
                    </div>
                    
                    <div className={styles.participantCardBody}>
                      <h2 className={styles.name}>
                        {[user.title, user.firstname, user.lastname].filter(Boolean).join(' ')}
                      </h2>
                      <p className={styles.position}>{user.jobtitle || ''}</p>
                      <p className={styles.firm}>
                        {user.firm?.abbrev && (
                          <>
                            <i className="bi bi-building"></i> {user.firm.abbrev}
                          </>
                        )}
                      </p>
                      {user.city?.name && user.country?.abbrev ? (
                        <span className={styles.location}>
                          <strong>{user.city.name} ({user.country.abbrev})</strong>
                        </span>
                      ) : (
                        <span className={styles.location}><strong></strong></span>
                      )}
                    </div>
                    
                    <div className={styles.participantCardFooter}>
                      <div className={styles.actionsWrapper}>
                        <div className={styles.actions}>
                          <a href={`mailto:${user.mail}`} className={styles.action}>
                            <i className="bi bi-envelope"></i> Email
                          </a>
                          {user.profile && (
                            <>
                              <div className={styles.divider}></div>
                              <a href={user.profile} className={styles.action} target="_blank" rel="noopener noreferrer">
                                <i className="bi bi-globe"></i> Web
                              </a>
                            </>
                          )}
                          {user.linkedin && (
                            <>
                              <div className={styles.divider}></div>
                              <a href={user.linkedin} className={styles.action} target="_blank" rel="noopener noreferrer">
                                <i className="bi bi-linkedin"></i> LinkedIn
                              </a>
                            </>
                          )}
                        </div>
                      </div>
                      
                      <div className={styles.meet}>
                        <input
                          type="checkbox"
                          id={`user-${user.User_ID}`}
                          checked={selectedUsers.has(user.User_ID)}
                          onChange={() => handleUserSelection(user.User_ID)}
                          className={styles.checkbox}
                        />
                        <label htmlFor={`user-${user.User_ID}`} className={styles.checkboxLabel}>
                          Meet with {user.firstname} {user.lastname}
                        </label>
                      </div>
                    </div>
                  </div>
                  ))
                )}
              </div>

              {/* Save Button */}
              <button
                className={`${styles.saveButton} btn btn-primary`}
                onClick={handleSaveSelection}
                disabled={saving}
              >
                {saving ? 'Saving...' : statusInfo.buttonText}
              </button>
            </>
          ) : (
            <div className={styles.voting}>
              {data.matches_generated ? (
                <>
                  <h1>✔️</h1>
                  <p>The matches have already been generated, so selection is no longer possible.</p>
                  <a href={`/events/${data.event.Events_ID}/rounds/`} className="btn btn-primary">
                    Watch the matches
                  </a>
                </>
              ) : (
                <>
                  <h1>✔️</h1>
                  <p>You have already selected your matches!<br />Continue with Match Ranking.</p>
                  <a href={`/events/${data.event.Events_ID}/vote/`} className="btn btn-primary">
                    Go to Match Ranking
                  </a>
                </>
              )}
            </div>
          )}
        </div>
      </div>
      <ToastContainer />
    </div>
  );
};

export default EventParticipantsPage;
